import type { IProductService } from './IProductService';
import type { PagedResponse } from '../dto/PagedResponse';
import type { StockLevel } from '../dto/StockLevel.dto';
import type { Product } from '../dto/Product.dto';
import type { UpdateProductRequest } from '../dto/UpdateProductRequest';
import type { CreateProductRequest } from '../dto/CreateProductRequest';
import type { ListParams } from '../http/ListParams';

import { QueryBuilder } from '../http/QueryBuilder';
import { JsonRequestBody } from '../http/JsonRequestBody';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { fetchWithAuth } from '../../../utils/fetch/FetchWithAuth';

const BASE = '/api/warehouse/product';
const DEFAULT_SORT = 'created_at:desc';

export class ProductService implements IProductService {
    async list(params?: ListParams): Promise<PagedResponse<Product>> {
        const hasPrimeNgSorting = Boolean(params?.sortField);
        const hasExplicitSorting = Boolean(params?.sort);

        const normalizedParams: ListParams = {
            ...params,
            ...(!hasPrimeNgSorting && !hasExplicitSorting
                ? { sort: DEFAULT_SORT }
                : {}),
        };

        return fetchWithAuth(
            `${BASE}${QueryBuilder.from(normalizedParams).build()}`,
            {
                method: 'GET',
            },
        );
    }

    async get(externalId: string): Promise<Product> {
        return fetchWithAuth(`${BASE}/${externalId}`, {
            method: 'GET',
        });
    }

    async create(payload: CreateProductRequest): Promise<Product> {
        const normalizedEan = payload.ean?.trim();

        const requestPayload: CreateProductRequest = {
            ...payload,
            ean: normalizedEan || null,
            use_as_component: payload.use_as_component ?? true,
            include_components: payload.include_components ?? true,
        };

        return fetchWithAuth(BASE, {
            method: 'POST',
            ...JsonRequestBody.from(requestPayload),
        });
    }

    async update(
        externalId: string,
        payload: UpdateProductRequest,
    ): Promise<Product> {
        const requestPayload: UpdateProductRequest = {
            ...payload,
            ...(Object.prototype.hasOwnProperty.call(payload, 'ean')
                ? {
                      ean:
                          typeof payload.ean === 'string'
                              ? payload.ean.trim() || null
                              : null,
                  }
                : {}),
        };

        return fetchWithAuth(`${BASE}/${externalId}`, {
            method: 'PATCH',
            ...JsonRequestBody.from(requestPayload),
        });
    }

    async updateStatus(
        externalId: string,
        status: string,
    ): Promise<Product> {
        return fetchWithAuth(`${BASE}/${externalId}/status`, {
            method: 'PATCH',
            ...JsonRequestBody.from({ status }),
        });
    }

    async getStock(externalId: string): Promise<StockLevel[]> {
        return fetchWithAuth(`${BASE}/${externalId}/stock`, {
            method: 'GET',
        });
    }
}